import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import MuiListItemAvatar from '@mui/material/ListItemAvatar';
import MuiListItemIcon from '@mui/material/ListItemIcon';
import MuiListItemText from '@mui/material/ListItemText';
import React from 'react';
import { Avatar } from '../avatar';
import { FlexBox } from '../box';
import { Checkbox } from '../checkbox';
import { CircularProgress, CircularProgressWithLabel } from '../circular-progress';
import { FacilityBadge } from '../facility-picker/badge';
import { getIcon } from '../icon';
import { Skeleton } from '../skeleton';
import { styled } from '../styled';
import { deepMerge } from '../utils';
const isPrimitive = (value) => {
    return typeof value === 'string' || typeof value === 'number';
};
const ItemWrapper = styled(FlexBox)(({ theme }) => ({
    '.MuiListItemSecondaryAction-root': { right: theme.spacing(1) },
    '.MuiListItemAvatar-root': {
        minWidth: 40,
        paddingRight: theme.spacing(2)
    }
}));
export const Item = React.forwardRef((props, ref) => {
    const { avatar, checkbox, icon, iconProps, primary, primaryRight, secondary, secondaryRight, selected, progress, facilityBadge, loading, className, sx, textSlotProps } = props;
    const mergedTextSlotProps = deepMerge({ primary: { variant: 'body2' } }, textSlotProps ?? {});
    const Icon = icon ? getIcon(icon) : () => null;
    const leftSlot = (() => {
        if (loading) {
            return (_jsx(MuiListItemIcon, { children: _jsx(Skeleton, { variant: "circular", width: 40, height: 40 }) }));
        }
        if (facilityBadge) {
            return (_jsx(MuiListItemIcon, { children: facilityBadge.onFetch ? (_jsx(FacilityBadge.Async, { label: facilityBadge.label, onFetch: facilityBadge.onFetch, moreText: facilityBadge.moreText })) : (_jsx(FacilityBadge, { facilityInfo: facilityBadge.facilityInfo, moreText: facilityBadge.moreText })) }));
        }
        if (progress) {
            if (progress.variant === 'indeterminate') {
                return (_jsx(MuiListItemIcon, { children: _jsx(CircularProgress, { color: "primary", ...progress }) }));
            }
            if (progress.value !== undefined) {
                return (_jsx(MuiListItemIcon, { children: _jsx(CircularProgressWithLabel, { color: "success", ...progress }) }));
            }
            console.warn('progress value is required in the determinated variant of CircularProgress component.');
        }
        if (checkbox !== undefined) {
            return (_jsx(MuiListItemIcon, { children: _jsx(Checkbox, { edge: "start", checked: selected, tabIndex: -1, disableRipple: true, indeterminate: checkbox === 'indeterminate' }) }));
        }
        if (avatar) {
            return (_jsx(MuiListItemAvatar, { children: iconProps ? (_jsx(Avatar, { ...avatar, title: undefined, children: _jsx(Icon, { ...iconProps }) })) : (_jsx(Avatar, { ...avatar })) }));
        }
        if (icon) {
            return (_jsx(MuiListItemIcon, { children: _jsx(Icon, { ...iconProps }) }));
        }
        return null;
    })();
    return (_jsxs(ItemWrapper, { stretch: true, vAlign: "center", ref: ref, sx: sx, className: className, children: [leftSlot, _jsxs(FlexBox, { hAlign: "space-between", gap: 2, stretch: true, vAlign: "center", children: [_jsx(MuiListItemText, { disableTypography: !isPrimitive(primary), primary: loading ? (_jsx(Skeleton, { variant: "text", width: "60%" })) : (primary), secondary: loading ? (_jsx(Skeleton, { variant: "text", width: "40%" })) : (secondary), slotProps: mergedTextSlotProps }), (primaryRight || secondaryRight) && (_jsx(MuiListItemText, { disableTypography: !isPrimitive(primaryRight), primary: primaryRight, secondary: secondaryRight, slotProps: {
                            primary: {
                                ...mergedTextSlotProps.primary,
                                ref: null,
                                ...mergedTextSlotProps.primaryRight,
                                textAlign: 'right'
                            },
                            secondary: {
                                ...mergedTextSlotProps.secondary,
                                ref: null,
                                ...mergedTextSlotProps.secondaryRight,
                                textAlign: 'right'
                            }
                        } }))] })] }));
});
Item.displayName = 'Item';
